import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { CloseButton } from '@/components/ds';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useLogger } from '@/hooks/useLogger';
import { Property } from '@/models/Property';
import { updatePropertyPrice } from '@/services/api/properties';
import { useTranslationPrefix } from '@/translations/i18n';

const font = "'Manrope', sans-serif";

export interface ManualPricePromptDialogProps {
  open: boolean;
  property: Property;
  onClose: () => void;
  onSaved: (updated: Property) => void;
}

export const enterListingPriceSx = {
  mt: 1,
  borderRadius: '10px',
  backgroundColor: 'var(--tx-lime)',
  color: '#262626',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#B0EF00' },
} as const;

export const ManualPricePromptInline = ({
  property,
  onSaved,
}: {
  property: Property;
  onSaved: (updated: Property) => void;
}) => {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const logger = useLogger('ManualPricePromptInline');
  const [raw, setRaw] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const parsed = Number(raw.replace(/[^0-9.]/g, ''));
  const isValid = Number.isFinite(parsed) && parsed > 0;

  const handleSave = async () => {
    if (!isValid || isSaving) return;
    setIsSaving(true);
    try {
      const updated = await updatePropertyPrice(property.id, parsed, 'manual');
      onSaved(updated);
    } catch (err) {
      logger.error('Failed to save manual property price', { error: err });
      showAlert(t('price.save_failed'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      sx={{
        background: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        width: 480,
        p: '25px',
        fontFamily: font,
      }}>
      <Typography
        sx={{ fontSize: 17, fontWeight: 600, color: 'var(--tx-text)', fontFamily: font, mb: '2px' }}>
        No list price
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'var(--tx-muted)', fontFamily: font, mb: '16px' }}>
        {property.address}
      </Typography>
      <Typography
        sx={{ fontSize: 13, color: 'var(--tx-muted)', fontFamily: font, lineHeight: 1.6, mb: '16px' }}>
        Enter your best estimate to enable ConditionScore — use recent comps or the list price if
        available.
      </Typography>
      <TextField
        autoFocus
        fullWidth
        value={raw}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, '');
          setRaw(digits ? Number(digits).toLocaleString('en-US') : '');
        }}
        disabled={isSaving}
        slotProps={{
          input: {
            startAdornment: (
              <Box
                component="span"
                sx={{ color: 'var(--tx-muted)', fontSize: 16, fontFamily: 'inherit', mr: '4px' }}>
                $
              </Box>
            ),
          },
        }}
        sx={{
          mb: '16px',
          '& .MuiInputBase-root': {
            backgroundColor: 'var(--tx-fill)',
            borderRadius: '10px',
            color: 'var(--tx-text)',
            fontSize: 16,
            padding: '0 12px',
            height: 40,
          },
          '& .MuiInputBase-input': {
            fontSize: 16,
            padding: '0',
            textAlign: 'right',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 0 },
          '& .MuiInputBase-root:hover, & .MuiInputBase-root.Mui-focused': { backgroundColor: 'var(--tx-fill-2)' },
        }}
      />
      <Box
        onClick={isValid && !isSaving ? handleSave : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: 40,
          borderRadius: '10px',
          backgroundColor: isValid ? 'var(--tx-lime)' : 'var(--tx-fill)',
          color: isValid ? 'var(--tx-on-lime)' : '#555',
          fontFamily: font,
          fontSize: 14,
          fontWeight: 600,
          cursor: isValid && !isSaving ? 'pointer' : 'default',
          transition: 'all 0.15s',
          '&:hover': isValid && !isSaving ? { backgroundColor: '#B0EF00' } : {},
        }}>
        {isSaving ? <CircularProgress size={16} sx={{ color: '#262626' }} /> : 'Save list price'}
      </Box>
    </Box>
  );
};

/**
 * Prompts an advisor to manually enter a listing price for a property when
 * none was detected by auto-enrichment. Off-market properties and newly
 * uploaded inspections without attached MLS data typically trigger this.
 * Saves to DynamoDB with priceSource='manual' so downstream scoring runs
 * and UI can badge the value as advisor-entered.
 */
export const ManualPricePromptDialog = ({
  open,
  property,
  onClose,
  onSaved,
}: ManualPricePromptDialogProps) => {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const logger = useLogger('ManualPricePromptDialog');
  const [raw, setRaw] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const parsed = Number(raw.replace(/[^0-9.]/g, ''));
  const isValid = Number.isFinite(parsed) && parsed > 0;

  const handleSave = async () => {
    if (!isValid || isSaving) return;
    setIsSaving(true);
    try {
      const updated = await updatePropertyPrice(property.id, parsed, 'manual');
      onSaved(updated);
      onClose();
    } catch (err) {
      logger.error('Failed to save manual property price', { error: err });
      showAlert(t('price.save_failed'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'none',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: 0,
            borderRadius: '25px',
            width: '100%',
            maxWidth: '480px',
            m: 3,
          },
        },
      }}>
      <CloseButton
        onClose={() => {
          if (!isSaving) onClose();
        }}
        label="Close"
        sx={{ position: 'absolute', top: 18, right: 18 }}
      />

      <DialogContent sx={{ p: '20px', pt: '62px', pb: '5px' }}>
        <Typography sx={{ fontSize: 14, color: 'var(--tx-muted)', lineHeight: 1.5, mb: '12px' }}>
          No price found for{' '}
          <Box component="span" sx={{ color: 'var(--tx-text)', fontWeight: 600 }}>
            {property.address}
          </Box>
          .
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'var(--tx-muted)', lineHeight: 1.5, mb: '25px' }}>
          Enter your best estimate to enable ConditionScore — use recent comps or the listing price
          if available. Accuracy matters here.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          value={raw}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^0-9]/g, '');
            setRaw(digits ? Number(digits).toLocaleString('en-US') : '');
          }}
          disabled={isSaving}
          slotProps={{
            input: {
              startAdornment: (
                <Box
                  component="span"
                  sx={{
                    color: 'var(--tx-muted)',
                    fontSize: 16,
                    fontFamily: 'inherit',
                    lineHeight: '16px',
                    display: 'inline-block',
                    mr: '4px',
                  }}>
                  $
                </Box>
              ),
            },
          }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: 'var(--tx-fill)',
              borderRadius: '10px',
              color: 'var(--tx-text)',
              fontSize: 16,
              padding: '0 12px',
              alignItems: 'center',
            },
            '& .MuiInputBase-input': {
              fontSize: 16,
              lineHeight: '16px',
              padding: '12px 0',
              textAlign: 'right',
            },
            '& .MuiOutlinedInput-notchedOutline': { border: 0 },
            '& .MuiInputBase-root:hover, & .MuiInputBase-root.Mui-focused': {
              backgroundColor: 'var(--tx-fill-2)',
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: '20px',
          py: '20px',
          gap: 1.5,
        }}>
        <Button
          onClick={onClose}
          disabled={isSaving}
          variant="text"
          sx={{ color: 'var(--tx-text)', fontSize: 14, fontWeight: 500 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isValid || isSaving}
          variant={isValid ? 'contained' : 'outlined'}
          startIcon={
            isSaving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : undefined
          }
          sx={{
            borderRadius: '10px',
            fontSize: 14,
            fontWeight: 500,
            ...(isValid
              ? {
                  backgroundColor: 'var(--tx-lime)',
                  color: '#262626',
                  border: 0,
                  '&:hover': { backgroundColor: '#B0EF00' },
                }
              : {
                  border: 0,
                  backgroundColor: 'var(--tx-fill)',
                  color: 'var(--tx-lime-ink)',
                  '&:hover': { backgroundColor: 'var(--tx-fill-2)' },
                }),
            '&.Mui-disabled': {
              border: 0,
              color: 'var(--tx-faint)',
              backgroundColor: 'var(--tx-fill)',
            },
          }}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
